import type {SuppliedFilterProviderOptions} from '@github-ui/filter-core'
import {NestedFilterProvider} from '@github-ui/filter-core/providers'
import {ssrSafeWindow} from '@github-ui/ssr-utils'

import {
  type BaseIssueFieldData,
  buildFieldSubKey,
  createNestedFieldProviderConfig,
  fetchIssueFieldData,
  mapNodeToFieldData,
} from './utils'

// Dashboard-specific interfaces for global search organization structure
export interface DashboardIssueFieldData extends BaseIssueFieldData {
  organization?: string
}

interface DashboardIssueFieldsResponse {
  issue_fields?: {
    [organization: string]: {
      fields: DashboardIssueFieldData[]
    }
  }
}

const DASHBOARD_ISSUE_FIELD_FILTER_ENDPOINT = '/_filter/issue_fields/dashboard'

// Cache for dashboard providers
export const dashboardProviderCache = new Map<string, Promise<NestedFilterProvider | null>>()

// Store known organizations from dashboard data for routing and suggestions
export const knownDashboardOrgs = new Set<string>()

export async function getDashboardIssueFieldFilterProvider(
  options: SuppliedFilterProviderOptions,
): Promise<NestedFilterProvider | null> {
  if (!ssrSafeWindow) {
    return null
  }

  const cacheKey = 'dashboard-issue-fields-global'

  // Return cached promise if it exists
  if (dashboardProviderCache.has(cacheKey)) {
    return dashboardProviderCache.get(cacheKey)!
  }

  // Create the promise and cache it immediately to prevent duplicate requests
  const providerPromise = createDashboardProvider(options)
  dashboardProviderCache.set(cacheKey, providerPromise)

  return providerPromise
}

async function createDashboardProvider(options: SuppliedFilterProviderOptions): Promise<NestedFilterProvider> {
  const endpoint = new URL(DASHBOARD_ISSUE_FIELD_FILTER_ENDPOINT, ssrSafeWindow!.location.origin)
  const response = await fetchIssueFieldData(endpoint.toString())

  if (!response) {
    const config = createNestedFieldProviderConfig('Filter by issue fields', [])
    return new NestedFilterProvider(config, options)
  }

  return createDashboardFieldProvider(response, options)
}

function createDashboardFieldProvider(
  dashboardData: DashboardIssueFieldsResponse,
  options: SuppliedFilterProviderOptions,
): NestedFilterProvider {
  const allSubKeys: Array<ReturnType<typeof buildFieldSubKey>> = []

  knownDashboardOrgs.clear()
  const orgNames = Object.keys(dashboardData?.issue_fields ?? {})
  for (const org of orgNames) {
    knownDashboardOrgs.add(org)
  }

  // Process each organization's fields
  for (const [orgName, orgData] of Object.entries(dashboardData?.issue_fields ?? {})) {
    for (const field of orgData.fields) {
      // Skip null/undefined fields or fields without valid data_type
      if (!field || !field.data_type) {
        continue
      }

      // Create a field with organization context
      const fieldWithOrg: DashboardIssueFieldData = {
        ...field,
        organization: orgName,
      }

      // Map to base format for utility function
      const baseField = mapNodeToFieldData(fieldWithOrg)

      // Build the sub key with organization-aware naming
      const subKey = buildFieldSubKey(baseField, options, orgName)
      if (subKey) {
        allSubKeys.push(subKey)
      }
    }
  }

  const config = createNestedFieldProviderConfig('Filter by issue fields', allSubKeys)
  // Note: Org-aware filtering is now handled by the useOrgAwareProviders hook
  const provider = new NestedFilterProvider(config, options)

  return provider
}
